import React, { useState, useEffect, useRef } from 'react';
import './Overlays.css';
import axios from 'axios';
import { useSocket } from '../../context/SocketContext';
import useDeviceSettings from '../../hooks/useDeviceSettings';

const OVERLAY_MESSAGE_MAX_LENGTH = 350;
const DEFAULT_OVERLAY_TRANSPARENCY = 60;
const OVERLAY_TYPES = ['bottom', 'top', 'full'];

const resolvePlaybackMode = (selectedDevice, playbackMode) => {
  if (playbackMode === 'system' || playbackMode === 'suitestream') return playbackMode;
  return selectedDevice?.interfaceMode === 'external_app' || selectedDevice?.externalApp
    ? 'system'
    : 'suitestream';
};

function Overlays({ site, selectedDevice, playbackMode = 'auto' }) {
  const [overlays, setOverlays] = useState([]);
  const [selectedOverlay, setSelectedOverlay] = useState(null);

  // Overlay fields
  const [overlayType, setOverlayType] = useState('bottom');
  const [overlayName, setOverlayName] = useState('');
  const [overlayMessage, setOverlayMessage] = useState('');
  const [overlayImageURL, setOverlayImageURL] = useState('');
  const [overlayDuration, setOverlayDuration] = useState(5);
  const [overlayTransparency, setOverlayTransparency] = useState(DEFAULT_OVERLAY_TRANSPARENCY);
  const [overlayTypeFilter, setOverlayTypeFilter] = useState('bottom');
  const { fadeDuration, setFadeDuration, updateDeviceSetting } = useDeviceSettings(selectedDevice);

  const socket = useSocket();
  const commandIdRef = useRef(1);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newOverlayName, setNewOverlayName] = useState('');

  const token = localStorage.getItem('token');
  const BASE_URL = process.env.REACT_APP_SOCKET_SERVER_URL || 'http://socket:5001';
  const resolvedPlaybackMode = resolvePlaybackMode(selectedDevice, playbackMode);

  const sendCommand = (command, value = null) => {
    if (!socket || !selectedDevice) return;
    const payload = {
      deviceId: selectedDevice.id,
      command,
      value,
      id: commandIdRef.current++,
    };
    socket.emit('sendCommand', payload);
  };

  useEffect(() => {
    if (!site) return;

    const fetchOverlays = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/overlays?siteId=${site.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list = response.data.overlays || [];
        setOverlays(
          list.filter((o) =>
            !String(o?.name || '').startsWith('Default Ads:') &&
            !String(o?.name || '').startsWith('Programme Creative:') &&
            String(o?.type || '').toLowerCase() !== 'watermark'
          )
        );
      } catch (error) {
        console.error('Error fetching overlays:', error);
        alert('Failed to fetch overlay ads.');
      }
    };

    fetchOverlays();
  }, [site, token, BASE_URL]);

  const handleOverlaySelect = (overlay) => {
    setSelectedOverlay(overlay);
    setOverlayType(overlay.type);
    setOverlayName(overlay.name || '');
    setOverlayMessage(overlay.message || '');
    setOverlayImageURL(overlay.image_url || '');
    setOverlayDuration(overlay.duration || 5);
    setOverlayTransparency(
      typeof overlay.transparency === 'number' ? overlay.transparency : DEFAULT_OVERLAY_TRANSPARENCY
    );
  };

  // Determine maximum message length and whether to show message
  const isFullOverlay = overlayType === 'full';
  const messageMaxLength = OVERLAY_MESSAGE_MAX_LENGTH;
  const showMessageField = overlayType !== 'full';

  useEffect(() => {
    if (overlayMessage.length > messageMaxLength) {
      setOverlayMessage((prev) => prev.slice(0, messageMaxLength));
    }
  }, [overlayMessage, messageMaxLength]);

  // Calculate remaining characters
  const messageRemaining = Math.max(messageMaxLength - overlayMessage.length, 0);
  const filteredOverlays = overlays.filter(
    (overlay) => String(overlay?.type || 'bottom').toLowerCase() === overlayTypeFilter
  );

  const handleTransparencyChange = (e) => {
    let value = parseInt(e.target.value, 10);
    if (Number.isNaN(value)) value = 0;
    value = Math.min(Math.max(value, 0), 100);
    setOverlayTransparency(value);
  };

  const handleSaveOverlay = async () => {
    if (!selectedOverlay) return;

    let updatedMessage = overlayMessage;
    // Hard truncate if user typed beyond limit
    if (updatedMessage.length > messageMaxLength) {
      updatedMessage = updatedMessage.slice(0, messageMaxLength);
      alert(`Message truncated to ${messageMaxLength} characters.`);
    }

    const updatedOverlay = {
      ...selectedOverlay,
      name: overlayName,
      type: overlayType,
      message: updatedMessage,
      image_url: overlayImageURL || selectedOverlay.image_url || null,
      qr_code_url: selectedOverlay.qr_code_url ?? null,
      duration: overlayDuration,
      transparency: overlayTransparency,
      image_asset_id: null,
      image_asset_kind: null,
      site_id: site ? site.id : null,
      campaign_id: selectedOverlay.campaign_id || null,
    };

    try {
      await axios.put(`${BASE_URL}/api/overlays/${selectedOverlay.id}`, updatedOverlay, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setOverlays((prev) =>
        prev.map((o) => (o.id === selectedOverlay.id ? updatedOverlay : o))
      );
      alert('Overlay Ad updated successfully!');
    } catch (error) {
      console.error('Error updating overlay:', error);
      alert('Failed to update overlay ad.');
    }
  };

  const handleFadeDurationChange = (e) => {
    if (!selectedDevice) return;
    let value = parseInt(e.target.value, 10);
    if (Number.isNaN(value) || value < 0) value = 0;
    if (value > 10) value = 10;
    setFadeDuration(value);
    updateDeviceSetting('fade_duration', value);
    sendCommand('setFadeDuration', { fadeDuration: value });
  };

  const handleAddNewOverlay = async () => {
    if (!newOverlayName.trim()) {
      alert('Please enter a name for the new overlay ad.');
      return;
    }
    if (!site) {
      alert('No site selected for this overlay ad.');
      return;
    }

    // Default to type=bottom, 7 second duration, link to site_id
    const newOverlayData = {
      name: newOverlayName.trim(),
      type: 'bottom',
      message: '',
      image_url: null,
      qr_code_url: null,
      duration: 7,
      transparency: DEFAULT_OVERLAY_TRANSPARENCY,
      image_asset_id: null,
      image_asset_kind: null,
      site_id: site.id,
      campaign_id: null,
    };

    try {
      const response = await axios.post(`${BASE_URL}/api/overlays`, newOverlayData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const createdOverlay = response.data.overlay;
      setOverlays((prev) => [...prev, createdOverlay]);
      setIsAddModalOpen(false);
      setSelectedOverlay(createdOverlay);
      setOverlayType('bottom');
      setOverlayName(createdOverlay.name);
      setOverlayMessage('');
      setOverlayImageURL('');
      setOverlayDuration(7);
      setOverlayTransparency(DEFAULT_OVERLAY_TRANSPARENCY);
    } catch (error) {
      console.error('Error adding new overlay:', error);
      alert('Failed to add new overlay ad.');
    }
  };

  const handlePlayOverlay = async () => {
    if (!selectedOverlay || !selectedOverlay.id) {
      alert('Please select a saved overlay ad first.');
      return;
    }
    if (!selectedDevice) {
      alert('No device selected.');
      return;
    }

    try {
      await axios.post(
        '/api/overlays/play',
        {
          deviceId: selectedDevice.id,
          overlayId: selectedOverlay.id,
          mode: resolvedPlaybackMode,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      alert('Overlay Ad played on the device!');
    } catch (error) {
      console.error('Error playing overlay:', error);
      alert('Failed to play overlay ad.');
    }
  };

  const handleRemoveOverlay = async () => {
    if (!selectedOverlay || !selectedOverlay.id) {
      alert('No overlay ad selected to remove.');
      return;
    }

    if (!window.confirm('Are you sure you want to remove this overlay?')) {
      return;
    }

    try {
      await axios.delete(`${BASE_URL}/api/overlays/${selectedOverlay.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOverlays((prev) => prev.filter((o) => o.id !== selectedOverlay.id));
      alert('Overlay Ad removed successfully!');
      setSelectedOverlay(null);
      setOverlayType('bottom');
      setOverlayName('');
      setOverlayMessage('');
      setOverlayImageURL('');
      setOverlayDuration(5);
      setOverlayTransparency(DEFAULT_OVERLAY_TRANSPARENCY);
    } catch (error) {
      console.error('Error removing overlay:', error);
      alert('Failed to remove overlay ad.');
    }
  };

  // Determine the label and description text for the image field
  let imageLabel = 'Overlay Ad Image or Webpage URL:';
  let imageDescription = '';
  if (overlayType === 'top') {
    imageLabel = 'Overlay Ad Image URL:';
    imageDescription = 'Add a url to the image you want to display';
  } else if (overlayType === 'full') {
    imageLabel = 'Overlay Ad Image or Webpage URL:';
    imageDescription = 'Add a url to an image or webpage, to be displayed Full Screen';
  }

  return (
    <div className="overlays-section">
      <div className="overlays-header">
        <h3>Overlay Ads</h3>
        <div className="overlay-actions">
          <button
            className="play-overlay-button"
            onClick={handlePlayOverlay}
            disabled={!selectedOverlay || !selectedOverlay.id}
          >
            Play Now
          </button>
          <button onClick={() => setIsAddModalOpen(true)} className="add-button">
            Add Overlay Ad
          </button>
        </div>
      </div>

      <div className="overlays-browser">
        <div className="overlays-list-header">
          <h4>Available Overlay Ads</h4>
          <span className="overlay-count">
            {filteredOverlays.length} shown / {overlays.length} saved
          </span>
        </div>
        <div className="overlay-filter-row">
          <div className="chooser-filters" aria-label="Overlay type filter">
            {OVERLAY_TYPES.map((type) => (
              <button
                key={type}
                type="button"
                className={overlayTypeFilter === type ? 'active' : ''}
                onClick={() => setOverlayTypeFilter(type)}
              >
                {type.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        <div className="overlay-grid">
          {filteredOverlays.length ? (
            filteredOverlays.map((overlay) => (
              <button
                type="button"
                key={overlay.id}
                className={`overlay-card ${
                  selectedOverlay && selectedOverlay.id === overlay.id ? 'selected' : ''
                }`}
                onClick={() => handleOverlaySelect(overlay)}
              >
                <div className="overlay-card__title">{overlay.name || 'Untitled Overlay Ad'}</div>
                <div className="overlay-card__meta">
                  <span className="overlay-pill">{(overlay.type || 'bottom').toUpperCase()}</span>
                  <span className="overlay-pill secondary">
                    {overlay.duration ? `${overlay.duration}s` : 'No duration'}
                  </span>
                </div>
                <div className="overlay-card__message">
                  {overlay.message ? overlay.message.slice(0, 80) : 'No message set'}
                </div>
              </button>
            ))
          ) : (
            <p className="overlay-empty">No {overlayTypeFilter} overlay ads yet.</p>
          )}
        </div>
      </div>

      <div className="overlay-details">
        {selectedOverlay ? (
          <>
            <h4>Edit Overlay Ad</h4>
            <label>
              Name:
              <input
                type="text"
                value={overlayName}
                onChange={(e) => setOverlayName(e.target.value)}
              />
            </label>

            <label>
              Type:
              <select
                value={overlayType}
                onChange={(e) => setOverlayType(e.target.value)}
              >
                <option value="bottom">Bottom</option>
                <option value="top">Top</option>
                <option value="full">Full</option>
              </select>
            </label>

            {showMessageField && (
              <label>
                Message ({messageRemaining} characters remaining):
                <textarea
                  placeholder="Enter overlay ad message..."
                  value={overlayMessage}
                  maxLength={messageMaxLength}
                  onChange={(e) => {
                    if (e.target.value.length <= messageMaxLength) {
                      setOverlayMessage(e.target.value);
                    }
                  }}
                />
              </label>
            )}

            <label>
              {imageLabel}
              <input
                type="text"
                value={overlayImageURL}
                onChange={(e) => setOverlayImageURL(e.target.value)}
              />
              {imageDescription && <small>{imageDescription}</small>}
            </label>

            {isFullOverlay && (
              <p className="overlay-note">
                Note: For Full overlay ads, the entire screen will be covered by the image or webpage URL.
              </p>
            )}

            {overlayType !== 'full' && (
              <label className="transparency-control">
                Transparency (%):
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={overlayTransparency}
                  onChange={handleTransparencyChange}
                  disabled={!selectedOverlay}
                />
                <div className="transparency-hint">0% = fully black background, 100% = fully transparent.</div>
                <div className="transparency-value">Current: {overlayTransparency}%</div>
              </label>
            )}

            <label>
              Duration (seconds):
              <input
                type="number"
                value={overlayDuration}
                onChange={(e) => setOverlayDuration(parseInt(e.target.value) || 1)}
                min={1}
              />
            </label>

            <label>
              Fade Duration (seconds):
              <input
                type="number"
                min={0}
                max={10}
                value={fadeDuration ?? ''}
                onChange={handleFadeDurationChange}
                disabled={!selectedDevice}
              />
            </label>

            <div className="overlay-buttons">
              <button onClick={handleSaveOverlay}>Save Overlay Ad</button>
              <button
                onClick={handlePlayOverlay}
                disabled={!selectedOverlay || !selectedOverlay.id}
              >
                Play Now
              </button>
              <button
                style={{ backgroundColor: '#dc2626', color: '#fff' }}
                onClick={handleRemoveOverlay}
              >
                Remove Overlay Ad
              </button>
            </div>
          </>
        ) : (
          <p>Select an overlay ad to edit or click "Add Overlay Ad".</p>
        )}
      </div>

      {isAddModalOpen && (
        <div className="add-overlay-modal">
          <div className="add-overlay-content">
            <h4>Add New Overlay Ad</h4>
            <input
              type="text"
              placeholder="Enter overlay ad name"
              value={newOverlayName}
              onChange={(e) => setNewOverlayName(e.target.value)}
            />
            <button onClick={handleAddNewOverlay}>Add</button>
            <button onClick={() => setIsAddModalOpen(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Overlays;
